import { Col, Form, Row } from '@valid-eval/shared-react-components';
import cx from 'classnames';
import { useMemo } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ContentPreview } from 'components/ContentsPreview';
import Input from 'components/Form/Input';
import RichText from 'components/Form/RichText';
import Select from 'components/Form/Select';
import { minLength } from 'components/FormFields/validations';
import { Communication } from 'data/features/communicationsTypes';
import { EventUser } from 'data/features/eventsTypes';

export type SubmitForReviewFormData = {
  organizer: string;
  subject: string;
  content: string;
};

type SubmitForReviewFormProps = {
  methods: UseFormReturn<SubmitForReviewFormData>;
  onSubmit: (data: SubmitForReviewFormData) => void;
  organizers: EventUser[];
  template?: Communication;
};

export const SubmitForReviewForm = ({
  methods,
  onSubmit,
  organizers,
  template,
}: SubmitForReviewFormProps) => {
  const { t } = useTranslation();

  // Get current content value for preview
  const content = methods.watch('content');

  let processedContent = content;
  Object.keys(template?.template_variables ?? {}).forEach((key) => {
    processedContent = processedContent.replaceAll(
      key,
      `<kbd>${template?.template_variables?.[key]?.preview_value}</kbd>`,
    );
  });

  const organizerOptions = useMemo(
    () =>
      organizers.map((organizer) => ({
        value: organizer.id,
        label: `${organizer.lastName}, ${organizer.firstName}`,
      })),
    [organizers],
  );

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Row className="mb-3">
          <Col md={6}>
            <Input
              name="subject"
              label={t('event.form.submit_for_review_subject')}
              placeholder={t('event.form.submit_for_review_subject_placeholder')}
              required
            />
          </Col>
          <Col md={6}>
            <Select
              name="organizer"
              label={t('event.form.submit_for_review_organizer')}
              placeholder={t('event.form.submit_for_review_organizer_placeholder')}
              options={organizerOptions}
              required
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <RichText
              required
              name="content"
              label={t('event.form.submit_for_review_content')}
              rules={{ validate: { minLength: minLength(100) } }}
            />
            <ContentPreview content={processedContent ?? ''}>
              {(content) => (
                <div className={cx('p-4 mt-2 border rounded shadow-sm bg-light')}>
                  <img
                    src="https://app.valideval.com/ve_email_logo.png"
                    alt="Valid Eval logo"
                    style={{ maxWidth: 249 }}
                  />
                  <hr />
                  {content}
                </div>
              )}
            </ContentPreview>
          </Col>
        </Row>
        <input type="submit" className="d-none" />
      </Form>
    </FormProvider>
  );
};

export default SubmitForReviewForm;
